const DEFAULT_TIMEOUT = 15000;
const RATE_LIMIT_WINDOW = 1000;
const MAX_REQUESTS_PER_WINDOW = 10;
const MAX_RETRIES = 2;
const RETRYABLE_STATUS_CODES = [408, 429, 500, 502, 503, 504];

const requestTimestamps = [];

const isRateLimited = () => {
  const now = Date.now();
  while (requestTimestamps.length > 0 && requestTimestamps[0] < now - RATE_LIMIT_WINDOW) {
    requestTimestamps.shift();
  }
  return requestTimestamps.length >= MAX_REQUESTS_PER_WINDOW;
};

const generateIdempotencyKey = () => {
  return `${Date.now()}-${crypto.randomUUID()}`;
};

export const secureFetch = async (url, options = {}) => {
  if (isRateLimited()) {
    throw new Error('Too many requests. Please wait a moment and try again.');
  }

  const { timeout = DEFAULT_TIMEOUT, idempotencyKey, retries = MAX_RETRIES, ...fetchOptions } = options;

  if (idempotencyKey) {
    fetchOptions.headers = {
      ...fetchOptions.headers,
      'Idempotency-Key': idempotencyKey,
    };
  }

  let lastError;
  for (let attempt = 0; attempt <= retries; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    requestTimestamps.push(Date.now());

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!response.ok && RETRYABLE_STATUS_CODES.includes(response.status) && attempt < retries) {
        lastError = new Error(`HTTP ${response.status}`);
        await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
        continue;
      }

      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      lastError = error;

      if (error.name === 'AbortError') {
        if (attempt < retries) {
          await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
          continue;
        }
        throw new Error('Request timed out. Please try again.');
      }

      if (attempt < retries) {
        await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
        continue;
      }

      throw error;
    }
  }

  throw lastError;
};

export { generateIdempotencyKey };
